import React, { useEffect, useState } from 'react';
import { trackApi, errMsg } from '../../lib/trackApi';
import DynamicField, { FIELD_TYPE_OPTIONS } from '../../components/track/DynamicField';
import { C, btn, btnDanger, btnSm, input as inputStyle, select as selectStyle, card, labelStyle } from '../../components/track/trackUi';

const FIXED = ['任務名稱', '內容', '發起人', '執行人', '狀態', '開始時間', '預計完成時間', '備註'];
const HAS_OPTIONS = new Set(['select', 'multiselect']);

function FieldCard({ field, members, onSave, onDelete, dragProps, highlight }) {
  const [label, setLabel] = useState(field.label);
  const [placeholder, setPlaceholder] = useState(field.placeholder || '');
  const [optionsText, setOptionsText] = useState((field.options || []).join('\n'));
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    setLabel(field.label);
    setPlaceholder(field.placeholder || '');
    setOptionsText((field.options || []).join('\n'));
  }, [field]);

  const saveOptions = () => {
    const opts = optionsText.split(/\n/).map((s) => s.trim()).filter(Boolean);
    if (JSON.stringify(opts) !== JSON.stringify(field.options || [])) onSave({ options: opts });
  };

  return (
    <div {...dragProps} style={{ ...card, marginBottom: 10, borderLeft: `4px solid ${C.brand}`, borderColor: highlight ? C.brand : C.border }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span title="拖曳排序" style={{ cursor: 'grab', color: '#9ca3af', userSelect: 'none' }}>⋮⋮</span>
        <input
          style={{ ...inputStyle, flex: '1 1 200px', fontWeight: 700 }}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => { if (label.trim() && label.trim() !== field.label) onSave({ label: label.trim() }); }}
        />
        <select style={selectStyle} value={field.fieldType} onChange={(e) => onSave({ fieldType: e.target.value })}>
          {FIELD_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, alignItems: 'flex-start' }}>
        {HAS_OPTIONS.has(field.fieldType) && (
          <div style={{ flex: '1 1 220px' }}>
            <label style={labelStyle}>選項（每行一個）</label>
            <textarea rows={4} style={{ ...inputStyle, width: '100%' }} value={optionsText} onChange={(e) => setOptionsText(e.target.value)} onBlur={saveOptions} />
          </div>
        )}
        <div style={{ flex: '1 1 220px' }}>
          <label style={labelStyle}>提示文字</label>
          <input
            style={{ ...inputStyle, width: '100%' }}
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            onBlur={() => { if (placeholder !== (field.placeholder || '')) onSave({ placeholder }); }}
          />
          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>預覽</label>
            <DynamicField field={{ ...field, placeholder }} value={preview} onChange={setPreview} members={members} />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 10, fontSize: 13, borderTop: '1px solid #f3f4f6', paddingTop: 8 }}>
        <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={field.isRequired} onChange={(e) => onSave({ isRequired: e.target.checked })} />
          必填
        </label>
        <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={field.showInList} onChange={(e) => onSave({ showInList: e.target.checked })} />
          列表顯示
        </label>
        <span style={{ color: '#9ca3af', fontSize: 11 }}>{field.key}</span>
        <button type="button" style={{ ...btnDanger, ...btnSm, marginLeft: 'auto' }} onClick={onDelete}>刪除</button>
      </div>
    </div>
  );
}

export default function TrackFieldDesigner({ boardId, fields, onChanged, members }) {
  const [list, setList] = useState(fields);
  const [newType, setNewType] = useState('text');
  const [newLabel, setNewLabel] = useState('');
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { setList(fields); }, [fields]);

  const run = async (fn) => {
    setError('');
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const add = () => {
    if (!newLabel.trim()) return;
    run(async () => {
      await trackApi.createField(boardId, { label: newLabel.trim(), fieldType: newType });
      setNewLabel('');
    });
  };

  const drop = (targetId) => {
    const from = list.findIndex((f) => f.id === dragId);
    const to = list.findIndex((f) => f.id === targetId);
    setDragId(null);
    setOverId(null);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...list];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setList(next);
    run(() => trackApi.reorderFields(boardId, next.map((f) => f.id)));
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ ...card, marginBottom: 12, background: '#fafafa' }}>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>固定欄位</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {FIXED.map((f) => <span key={f} style={{ fontSize: 12, background: '#e5e7eb', borderRadius: 4, padding: '2px 8px' }}>{f}</span>)}
        </div>
      </div>

      {error && <div style={{ color: C.danger, marginBottom: 8 }}>{error}</div>}

      {list.map((f) => (
        <FieldCard
          key={f.id}
          field={f}
          members={members}
          highlight={overId === f.id && dragId !== f.id}
          dragProps={{
            draggable: true,
            onDragStart: (e) => {
              if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) { e.preventDefault(); return; }
              setDragId(f.id);
            },
            onDragOver: (e) => { e.preventDefault(); setOverId(f.id); },
            onDragEnd: () => { setDragId(null); setOverId(null); },
            onDrop: (e) => { e.preventDefault(); drop(f.id); },
          }}
          onSave={(patch) => run(() => trackApi.patchField(f.id, patch))}
          onDelete={() => {
            if (window.confirm(`刪除欄位「${f.label}」？已填的資料將不再顯示。`)) run(() => trackApi.deleteField(f.id));
          }}
        />
      ))}

      <div style={{ ...card, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', borderStyle: 'dashed' }}>
        <input
          style={{ ...inputStyle, flex: '1 1 200px' }}
          placeholder="新問題名稱"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
        />
        <select style={selectStyle} value={newType} onChange={(e) => setNewType(e.target.value)}>
          {FIELD_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button type="button" style={btn} disabled={!newLabel.trim()} onClick={add}>＋ 新增欄位</button>
      </div>
    </div>
  );
}
